import React from "react";
import { Box, Collapse, Switch, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckBoxOutlineBlankRoundedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import { FormControlLabel, Checkbox, Popover } from "@mui/material";
import { ReactComponent as InfoIcon } from "assets/icons/Bredcrum_Icon/infoIcon.svg";
import Tooltip from "components/ui/Tooltip";
import "./NewRightsPage.css";

const RightsTable = ({
  data,
  checkedState,
  permissionSelection,
  expanded,
  onToggleExpand,
  onSwitchChange,
  onOptionToggle,
  onPopoverOpen,
  currentPopoverItem,
  anchorEl,
  onPopoverClose,
  isDisabled = false,
  isAdmin = false,
  renderCustomLabel = null,
  getDisableReason = null,
  isScanApp = false,
  isNexusWeb = false,
  isClientMasterApp = false,
  getRestrictedOptionsForItem = () => [],
  selectedUserType = "Standard User",
}) => {
  const popoverOpen = Boolean(anchorEl);

  const mainToggleKey = isScanApp ? (data?.find((item) => item.isMainToggle)?.name || "Scan App Access") : null;
  const isScanAppAccessEnabled = isScanApp ? !!checkedState[mainToggleKey] : true;
  const isNexusWebAccessEnabled = isNexusWeb ? !!checkedState["Nexus Web Access"] : true;
  const isClientMasterAppAccessEnabled = isClientMasterApp ? !!checkedState["Client Master App Access"] : true;
  const isCadUser = selectedUserType === "CAD User";
  const cadOnlyViewMenus = ["CAD", "CAM", "Sizing", "Modification"];
  const isAccessMenuItem = (itemKey) => {
    return itemKey?.includes("_Access") || itemKey?.endsWith("Access");
  };

  const shouldShowOnlyView = (itemKey) => {
    if (!isCadUser) return false;
    const parentMenu = itemKey?.split("_")[0];
    if (parentMenu === "CAD" && isAccessMenuItem(itemKey)) return false;
    return cadOnlyViewMenus.includes(parentMenu) && isAccessMenuItem(itemKey);
  };
  const getFilteredOptions = (options, itemKey) => {
    if (shouldShowOnlyView(itemKey)) {
      return ["View"];
    }
    const restricted = getRestrictedOptionsForItem(itemKey);
    if (restricted.length > 0) {
      return options.filter((opt) => !restricted.includes(opt));
    }
    return options;
  };

  const renderOptionsDisplay = (item, itemKey, isChecked, options, selectedOptions, isItemDisabled) => {
    const filteredOptions = getFilteredOptions(options, itemKey);
    const hasMultipleOptions = filteredOptions?.length > 1;
    const isPopoverOpen = currentPopoverItem?.subItemName === itemKey || currentPopoverItem?.name === itemKey;
    const restrictedOptions = getRestrictedOptionsForItem(itemKey);
    let validSelectedOptions = selectedOptions;
    if (shouldShowOnlyView(itemKey)) {
      validSelectedOptions = selectedOptions.filter((opt) => opt === "View");
    }

    const shouldKeepFullOptionsVisible = isChecked && restrictedOptions.length > 0;
    const displayOptions = shouldKeepFullOptionsVisible ? filteredOptions : !isChecked ? filteredOptions : validSelectedOptions;
    const hint = item?.infoTooltip;
    const shouldHideTooltip = isCadUser;

    const optionsBlock = (
      <div
        className="options-display"
        onClick={(e) => {
          if (isDisabled || isItemDisabled) return;
          if (isChecked && hasMultipleOptions && !shouldShowOnlyView(itemKey)) {
            e.stopPropagation();
            onPopoverOpen(e, {
              ...item,
              isSubMenu: true,
              subItemName: itemKey,
              options: filteredOptions,
            });
          }
        }}
        style={{
          opacity: isDisabled || isItemDisabled ? 0.6 : 1,
          cursor: isDisabled || isItemDisabled ? "default" : "pointer",
        }}
      >
        <Typography
          className="options-text"
          sx={{
            flex: 1,
            minWidth: 0,
            margin: 0,
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "24px",
            maxWidth: "40vw",
          }}
        >
          {(displayOptions || [])?.map((opt, index, arr) => (
            <span
              key={opt}
              style={{
                color: isChecked && validSelectedOptions?.includes(opt) ? "#fff" : "#666",
                fontFamily: "Karla, sans-serif",
                fontWeight: 500,
                fontSize: "18px",
              }}
            >
              {opt}
              {index < arr.length - 1 && ", "}
            </span>
          ))}
        </Typography>
        {isChecked && hasMultipleOptions && !shouldShowOnlyView(itemKey) && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "16px",
              color: "#fff",
              transform: isPopoverOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        )}
      </div>
    );

    if (!hint || shouldHideTooltip) return optionsBlock;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
        {optionsBlock}
        <Tooltip title={hint} placement="bottom">
          <Box
            component="span"
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "inline-flex",
              flexShrink: 0,
              cursor: "default",
              alignItems: "center",
              lineHeight: 0,
              opacity: isDisabled || isItemDisabled ? 0.5 : !isChecked ? 0.55 : 1,
              filter: isDisabled || isItemDisabled ? "grayscale(1) brightness(0.75)" : !isChecked ? "grayscale(1) brightness(0.65)" : "none",
            }}
          >
            <InfoIcon width={20} height={20} />
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const renderRow = (item, itemKey, level = 0, isSubItem = false) => {
    const isChecked = !!checkedState[itemKey];
    let selectedOptions = permissionSelection[itemKey] || [];
    const originalOptions = item.options || [];
    const filteredOptions = getFilteredOptions(originalOptions, itemKey);
    if (shouldShowOnlyView(itemKey)) {
      selectedOptions = selectedOptions.filter((opt) => opt === "View");
    }

    let disableReason = getDisableReason ? getDisableReason(item, itemKey) : null;

    if (isScanApp && !isSubItem && itemKey !== mainToggleKey) {
      if (!isScanAppAccessEnabled) {
        disableReason = "Scan App Access required";
      }
    }

    const isItemDisabled = isDisabled || !!disableReason;

    return (
      <div key={itemKey} className="row" style={{ marginLeft: level * 20 }}>
        <div className="row-content">
          {renderCustomLabel ? (
            renderCustomLabel(item, itemKey)
          ) : (
            <Typography className="row-label" style={{ color: isItemDisabled ? "#888" : "#fff" }}>
              {item.label || item.name}
            </Typography>
          )}

          <div className="right-content row-actions">
            <Switch
              className="ios-switch"
              checked={isChecked}
              disableRipple
              disabled={isItemDisabled}
              onClick={(e) => {
                if (isItemDisabled) return;
                e.stopPropagation();
                onSwitchChange(itemKey, filteredOptions, level === 0);
              }}
              sx={{
                opacity: isItemDisabled || isAdmin ? 0.6 : 1,
                "& .MuiSwitch-thumb": {
                  backgroundColor: isAdmin || isItemDisabled ? "#888" : "#fff",
                },
              }}
            />

            {filteredOptions?.length > 0 && renderOptionsDisplay(item, itemKey, isChecked, filteredOptions, selectedOptions, isItemDisabled)}
          </div>
        </div>
      </div>
    );
  };

  const renderMainItem = (mainItem) => {
    const isExpanded = expanded[mainItem.name];
    const hasSubMenus = mainItem.subMenus?.length > 0;

    // Flat isScanApp items (Fab/Breco/Aira style) — no subMenus, not main toggle
    if (isScanApp && !hasSubMenus && !mainItem.isMainToggle) {
      return renderFlatItem(mainItem);
    }
    // Special case: "Sets Access" should not have expand toggle - it has a switch directly on it
    const isSetsAccess = mainItem.name === "Sets Access";
    const hasExpandToggle = hasSubMenus && !mainItem.noExpand && !isSetsAccess;
    const isScanAppAccessItem = isScanApp && !!mainItem.isMainToggle;

    const mainDisableReason = getDisableReason ? getDisableReason(mainItem, mainItem.name) : null;
    const isMainItemDisabled = isDisabled || !!mainDisableReason;

    let isMainItemDisabledForScan = false;
    if (isScanApp && !isScanAppAccessItem && !isScanAppAccessEnabled) {
      isMainItemDisabledForScan = true;
    }

    // For Sets Access, render as flat item with switch directly on the row
    if (isSetsAccess && !isScanApp) {
      const setsKey = "Sets Access_Sets Access";
      const isChecked = !!checkedState[setsKey];
      let selectedOptions = permissionSelection[setsKey] || [];
      const setsSubItem = mainItem.subMenus?.[0];
      const filteredSetOptions = getFilteredOptions(setsSubItem?.options || [], setsKey);
      if (shouldShowOnlyView(setsKey)) {
        selectedOptions = selectedOptions.filter((opt) => opt === "View");
      }

      return (
        <Box key={mainItem.name} style={{ marginBottom: "8px" }}>
          <div className="row">
            <div className="row-content">
              <Typography className="row-label">{mainItem.name}</Typography>
              <div className="right-content row-actions">
                <Switch
                  className="ios-switch"
                  checked={isChecked}
                  disableRipple
                  disabled={isDisabled}
                  onClick={(e) => {
                    if (isDisabled) return;
                    e.stopPropagation();
                    onSwitchChange(setsKey, filteredSetOptions);
                  }}
                  sx={{
                    opacity: isDisabled || isAdmin ? 0.6 : 1,
                    "& .MuiSwitch-thumb": {
                      backgroundColor: isAdmin ? "#888" : "#fff",
                    },
                  }}
                />
                {filteredSetOptions?.length > 0 && (
                  <div
                    className="options-display"
                    onClick={(e) => {
                      if (isDisabled) return;
                      if (isChecked && (filteredSetOptions.length > 1 || filteredSetOptions[0] !== "View") && !shouldShowOnlyView(setsKey)) {
                        e.stopPropagation();
                        onPopoverOpen(e, {
                          ...setsSubItem,
                          isSubMenu: true,
                          subItemName: setsKey,
                          options: filteredSetOptions,
                        });
                      }
                    }}
                    style={{
                      opacity: isDisabled || isAdmin ? 0.6 : 1,
                      cursor: isDisabled ? "default" : "pointer",
                    }}
                  >
                    <Typography className="options-text">
                      {!isChecked
                        ? filteredSetOptions.map((opt, index) => (
                            <span key={opt} style={{ color: "#666", fontFamily: "Karla, sans-serif", fontWeight: 500, fontSize: "18px" }}>
                              {opt} {index < filteredSetOptions.length - 1 && ", "}
                            </span>
                          ))
                        : selectedOptions.length > 0
                          ? selectedOptions
                              .filter((opt) => filteredSetOptions.includes(opt))
                              .map((opt, index) => (
                                <span key={opt} style={{ color: "#fff", fontFamily: "Karla, sans-serif", fontWeight: 500, fontSize: "18px" }}>
                                  {opt} {index < selectedOptions.filter((opt) => filteredSetOptions.includes(opt)).length - 1 && ", "}
                                </span>
                              ))
                          : null}
                    </Typography>
                    {isChecked && (filteredSetOptions.length > 1 || filteredSetOptions[0] !== "View") && !shouldShowOnlyView(setsKey) && (
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: "16px",
                          color: "#fff",
                          transform: currentPopoverItem?.subItemName === setsKey && Boolean(anchorEl) ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Box>
      );
    }

    return (
      <Box key={mainItem.name} style={{ marginBottom: "8px" }}>
        <div
          className="row"
          onClick={() => {
            if (hasExpandToggle && !isMainItemDisabled && !isMainItemDisabledForScan) {
              onToggleExpand(mainItem.name);
            }
          }}
          style={{
            cursor: hasExpandToggle && !isMainItemDisabled && !isMainItemDisabledForScan ? "pointer" : "default",
            borderBottom: isScanAppAccessItem ? "none" : undefined,
            paddingBottom: isScanAppAccessItem ? "4px" : undefined,
          }}
        >
          <div className="row-content">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {hasExpandToggle &&
                (isExpanded ? (
                  <KeyboardArrowDownIcon sx={{ color: isMainItemDisabled ? "#888" : "#fff", fontSize: "20px" }} />
                ) : (
                  <KeyboardArrowRightIcon sx={{ color: isMainItemDisabled ? "#888" : "#fff", fontSize: "20px" }} />
                ))}
              <Typography
                className="row-label"
                style={{
                  width: isScanAppAccessItem ? "auto" : "100%",
                  flex: isScanAppAccessItem ? "0 0 auto" : 1,
                  fontSize: isScanAppAccessItem ? "20px" : undefined,
                  whiteSpace: isScanAppAccessItem ? "nowrap" : undefined,
                  marginRight: isScanAppAccessItem ? "16px" : undefined,
                  color: isMainItemDisabled ? "#888" : "#fff",
                }}
              >
                {mainItem.name}
              </Typography>
            </div>

            <div className="right-content row-actions">
              {isScanAppAccessItem && (
                <Switch
                  className="ios-switch"
                  checked={!!checkedState[mainItem.name]}
                  disableRipple
                  disabled={isDisabled}
                  onClick={(e) => {
                    if (isDisabled) return;
                    e.stopPropagation();
                    onSwitchChange(mainItem.name, []);
                  }}
                  sx={{
                    opacity: isDisabled || isAdmin ? 0.6 : 1,
                    "& .MuiSwitch-thumb": {
                      backgroundColor: isAdmin ? "#888" : "#fff",
                    },
                  }}
                />
              )}

              {/* For items without submenus */}
              {!hasSubMenus && !isScanAppAccessItem && renderRow(mainItem, mainItem.name, 0, false)}
            </div>
          </div>
        </div>

        {hasSubMenus && (
          <Collapse in={isExpanded}>
            <div style={{ backgroundColor: "#000000ff", overflow: "hidden", height: "auto" }}>
              {mainItem.subMenus.map((subItem) => {
                let subKey;
                if (isScanApp) {
                  subKey = subItem.name;
                } else {
                  subKey = `${mainItem.name}_${subItem.name}`;
                }
                return renderRow(subItem, subKey, 1, true);
              })}
            </div>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderFlatItem = (item) => {
    const isNexusWebAccessItem = isNexusWeb && item.name === "Nexus Web Access";
    const isClientMasterAppAccessItem = isClientMasterApp && item.name === "Client Master App Access";
    const isChecked = !!checkedState[item.name];
    let selectedOptions = permissionSelection[item.name] || [];
    const originalOptions = item.options || [];
    const filteredOptions = getFilteredOptions(originalOptions, item.name);
    if (shouldShowOnlyView(item.name)) {
      selectedOptions = selectedOptions.filter((opt) => opt === "View");
    }

    const disableReason = getDisableReason ? getDisableReason(item, item.name) : null;
    let isItemDisabled = isDisabled || !!disableReason;
    if (isNexusWeb && !isNexusWebAccessItem && !isNexusWebAccessEnabled) {
      isItemDisabled = true;
    }
     if (isClientMasterApp && !isClientMasterAppAccessItem && !isClientMasterAppAccessEnabled) {
      isItemDisabled = true;
    }
    if (isNexusWebAccessItem) {
      return (
        <div key={item.name} className="row" style={{ borderBottom: "none", paddingBottom: "4px" }}>
          <div className="row-content">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Typography className="row-label" style={{ width: "200px", flex: "0 0 auto", fontSize: "20px", color: isDisabled ? "#888" : "#fff" }}>
                {item.name}
              </Typography>
            </div>
            <div className="right-content row-actions">
              <Switch
                className="ios-switch"
                checked={isChecked}
                disableRipple
                disabled={isDisabled}
                onClick={(e) => {
                  if (isDisabled) return;
                  e.stopPropagation();
                  onSwitchChange(item.name, filteredOptions, true);
                }}
                sx={{
                  opacity: isDisabled || isAdmin ? 0.6 : 1,
                  "& .MuiSwitch-thumb": {
                    backgroundColor: isAdmin || isDisabled ? "#888" : "#fff",
                  },
                }}
              />
              {filteredOptions?.length > 0 && renderOptionsDisplay(item, item.name, isChecked, filteredOptions, selectedOptions, isDisabled)}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div key={item.name} className="row">
        <div className="row-content">
          <Typography className="row-label" style={{ color: isItemDisabled ? "#888" : "#fff" }}>
            {item.name}
          </Typography>
          <div className="right-content row-actions">
            <Switch
              className="ios-switch"
              checked={isChecked}
              disableRipple
              disabled={isItemDisabled}
              onClick={(e) => {
                if (isItemDisabled) return;
                e.stopPropagation();
                onSwitchChange(item.name, filteredOptions, true);
              }}
              sx={{
                opacity: isItemDisabled || isAdmin ? 0.6 : 1,
                "& .MuiSwitch-thumb": {
                  backgroundColor: isAdmin || isItemDisabled ? "#888" : "#fff",
                },
              }}
            />
            {filteredOptions?.length > 0 && renderOptionsDisplay(item, item.name, isChecked, filteredOptions, selectedOptions, isItemDisabled)}
          </div>
        </div>
      </div>
    );
  };

  if (isScanApp) {
    return (
      <Box>
        {data.map(renderMainItem)}

        <Popover
          open={popoverOpen}
          anchorEl={anchorEl}
          onClose={onPopoverClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              backgroundColor: "#fff",
              color: "#000",
              padding: "8px 12px",
              borderRadius: "6px",
              marginTop: "6px",
              boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
              maxHeight: "250px",
              overflowY: "auto",
              width: "fit-content",
              minWidth: "auto",
            },
          }}
        >
          {currentPopoverItem && (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {currentPopoverItem.options
                ?.filter((option) => {
                  const itemKey = currentPopoverItem?.isSubMenu ? currentPopoverItem?.subItemName : currentPopoverItem?.name;
                  const isCadUserFilter = isCadUser;
                  const cadOnlyViewMenusFilter = ["CAD", "CAM", "Sizing", "Modification"];
                  const parentMenuFilter = itemKey?.split("_")[0];
                  const isAccessFilter = isAccessMenuItem(itemKey);
                  if (isCadUserFilter && cadOnlyViewMenusFilter.includes(parentMenuFilter) && isAccessFilter && parentMenuFilter !== "CAD") {
                    return option === "View";
                  }
                  return !getRestrictedOptionsForItem(itemKey).includes(option);
                })
                ?.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        icon={<CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 23, color: "#474747ff" }} />}
                        checkedIcon={<CheckBoxRoundedIcon sx={{ fontSize: 23, color: "#50C878" }} />}
                        checked={currentPopoverItem.isSubMenu ? permissionSelection[currentPopoverItem.subItemName]?.includes(option) : permissionSelection[currentPopoverItem.name]?.includes(option)}
                        onChange={() => onOptionToggle(currentPopoverItem.isSubMenu ? currentPopoverItem.subItemName : currentPopoverItem.name, option)}
                        sx={{ m: 0, p: 0, ml: 1 }}
                      />
                    }
                    label={<Typography sx={{ fontFamily: "Karla, sans-serif", fontWeight: 400, fontSize: "14px", lineHeight: "32px", color: "#000", marginLeft: "6px" }}>{option}</Typography>}
                  />
                ))}
            </Box>
          )}
        </Popover>
      </Box>
    );
  }

  const isHierarchical = data?.some((item) => item.subMenus?.length > 0 && item.name !== "Sets Access");

  return (
    <Box>
      {isHierarchical ? data.map(renderMainItem) : data.map(renderFlatItem)}

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={onPopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            color: "#000",
            padding: "8px 12px",
            borderRadius: "6px",
            marginTop: "6px",
            boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
            maxHeight: "250px",
            overflowY: "auto",
            width: "fit-content",
            minWidth: "auto",
          },
        }}
      >
        {currentPopoverItem && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {currentPopoverItem.options
              ?.filter((option) => {
                const itemKey = currentPopoverItem?.isSubMenu ? currentPopoverItem?.subItemName : currentPopoverItem?.name;
                const isCadUserFilter = isCadUser;
                const cadOnlyViewMenusFilter = ["CAD", "CAM", "Sizing", "Modification"];
                const parentMenuFilter = itemKey?.split("_")[0];
                const isAccessFilter = isAccessMenuItem(itemKey);
                if (isCadUserFilter && cadOnlyViewMenusFilter.includes(parentMenuFilter) && isAccessFilter) {
                  return option === "View";
                }
                return !getRestrictedOptionsForItem(itemKey).includes(option);
              })
              ?.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      icon={<CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 23, color: "#474747ff" }} />}
                      checkedIcon={<CheckBoxRoundedIcon sx={{ fontSize: 23, color: "#50C878" }} />}
                      checked={currentPopoverItem.isSubMenu ? permissionSelection[currentPopoverItem.subItemName]?.includes(option) : permissionSelection[currentPopoverItem.name]?.includes(option)}
                      onChange={() => onOptionToggle(currentPopoverItem.isSubMenu ? currentPopoverItem.subItemName : currentPopoverItem.name, option)}
                      sx={{ m: 0, p: 0, ml: 1 }}
                    />
                  }
                  label={<Typography sx={{ fontFamily: "Karla, sans-serif", fontWeight: 400, fontSize: "14px", lineHeight: "32px", color: "#000", marginLeft: "6px" }}>{option}</Typography>}
                />
              ))}
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default RightsTable;
